import { useState, useEffect } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface CloneApiDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newApiName: string) => void;
  originalApiUrl?: string;
  onCloseAutoFocus?: (event: Event) => void;
}

export function CloneApiDialog({
  isOpen,
  onClose,
  onConfirm,
  originalApiUrl = "API",
  onCloseAutoFocus,
}: CloneApiDialogProps) {
  const [newApiName, setNewApiName] = useState(`${originalApiUrl} (clone)`);

  useEffect(() => {
    if (isOpen) {
      setNewApiName(`${originalApiUrl} (clone)`);
    }
  }, [originalApiUrl, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newApiName.trim()) {
      onConfirm(newApiName.trim());
      setNewApiName(`${originalApiUrl} (clone)`);
      onClose();
    }
  };

  const handleClose = () => {
    setNewApiName(`${originalApiUrl} (clone)`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle className="text-white">Clone API Endpoint</DialogTitle>
          <DialogDescription className="sr-only">
            Enter a name for the cloned API endpoint and confirm to create a copy.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="py-4">
            <Label htmlFor="cloneName" className="text-white">
              New API Name
            </Label>
            <Input
              id="cloneName"
              value={newApiName}
              onChange={(e) => setNewApiName(e.target.value)}
              className="mt-2 bg-surface-input border-surface-border text-white placeholder:text-muted focus:ring-primary"
              placeholder="Enter new API name"
              autoFocus
            />
          </div>
          <DialogFooter>
            {/* Route Cancel through Radix's Close primitive — see
                clone-persona-dialog for the DEF-792-02 rationale. */}
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary-dark text-white"
              disabled={!newApiName.trim()}
            >
              Clone API
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}